import { Radio, Text } from '@mantine/core';
import { clsx } from 'clsx';
import { useIntl } from 'react-intl';

import { freightRateCardMessages } from './freight-rate-card-messages';
import styles from './RateSelector.module.css';

import type { LtlQuote } from '../../types/ltl';

type RateSelectorProps = {
  quote: LtlQuote | null;
  label: string;
  isSelected: boolean;
  onSelect: (quote: LtlQuote) => void;
};

export const RateSelector = ({ quote, label, isSelected, onSelect }: RateSelectorProps) => {
  const intl = useIntl();

  const handleSelect = () => {
    if (quote) {
      onSelect(quote);
    }
  };

  if (!quote) {
    return (
      <div aria-disabled className={clsx(styles.selector, styles.selectorUnavailable)} data-testid='rate-selector-unavailable'>
        <div className={styles.middleRow}>
          <Text className={styles.unavailable} component='span'>
            {intl.formatMessage(freightRateCardMessages.notAvailable, { label })}
          </Text>
          <Radio aria-label={label} checked={false} disabled readOnly size='xs' tabIndex={-1} />
        </div>
      </div>
    );
  }

  return (
    <div
      aria-pressed={isSelected}
      className={clsx(styles.selector, isSelected && styles.selectorSelected)}
      data-testid={`rate-selector-${quote.offerId}`}
      onClick={handleSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSelect();
        }
      }}
      role='button'
      tabIndex={0}
    >
      <div className={styles.topLine}>
        <span className={styles.label}>{label}</span>
      </div>
      <div className={styles.middleRow}>
        <span className={styles.price}>{`$${quote.totalCharges.toFixed(2)}`}</span>
        <Radio aria-label={label} checked={isSelected} onChange={handleSelect} readOnly size='xs' tabIndex={-1} />
      </div>
      <Text className={styles.transit} component='span'>
        {intl.formatMessage(freightRateCardMessages.transitDays, { days: quote.transitDays })}
      </Text>
    </div>
  );
};
